import { FormikConfig } from "formik";
import { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { FormValues, FindingItem, NextStepItem, Translations } from "./types";
import { DashboardAppointmentAIReport } from "../AppointmentAIReportsTableContainer/getAppointmentAIReports";

type FormikProps = FormikConfig<FormValues>;
type SubmitHandler = FormikConfig<FormValues>["onSubmit"];

const useFormikProps = (params: {
  report: DashboardAppointmentAIReport | null;
  onSave: (values: FormValues) => void | Promise<void>;
  handleClose: () => void;
  translations: Translations;
}): FormikProps => {
  const { report, onSave, handleClose, translations } = params;

  const initialValues = useMemo<FormValues>(() => {
    let findingsSection: FindingItem[] = [];
    let nextStepsSection: NextStepItem[] = [];

    // Parse findings_section if it exists
    if (report?.findings_section) {
      try {
        if (typeof report.findings_section === "string") {
          const parsed = JSON.parse(report.findings_section);
          if (Array.isArray(parsed)) {
            findingsSection = parsed as FindingItem[];
          }
        } else if (Array.isArray(report.findings_section)) {
          findingsSection = report.findings_section as unknown as FindingItem[];
        }
      } catch (e) {
        // Error parsing findings_section
        findingsSection = [];
      }
    }

    // Parse next_steps_section if it exists
    if (report?.next_steps_section) {
      try {
        if (typeof report.next_steps_section === "string") {
          const parsed = JSON.parse(report.next_steps_section);
          if (Array.isArray(parsed)) {
            nextStepsSection = parsed as NextStepItem[];
          }
        } else if (Array.isArray(report.next_steps_section)) {
          nextStepsSection =
            report.next_steps_section as unknown as NextStepItem[];
        }
      } catch (e) {
        // Error parsing next_steps_section
        nextStepsSection = [];
      }
    }

    return {
      summarySection: report?.summary_section || "",
      findingsSection,
      nextStepsSection,
    };
  }, [report]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        findingsSection: Yup.array().of(
          Yup.object({
            severity: Yup.string().required(translations.errorSeverityRequired),
            title: Yup.string().required(translations.errorTitleRequired),
            whatThisMeans: Yup.string().required(
              translations.errorWhatThisMeansRequired,
            ),
            whyItMatters: Yup.string().required(
              translations.errorWhyItMattersRequired,
            ),
          }),
        ),
        nextStepsSection: Yup.array().of(
          Yup.object({
            title: Yup.string().required(translations.errorTitleRequired),
            description: Yup.string().required(
              translations.errorDescriptionRequired,
            ),
          }),
        ),
      }),
    [translations],
  );

  const onSubmit = useCallback<SubmitHandler>(
    async (values, { resetForm }) => {
      await onSave(values);
      handleClose();
      resetForm();
    },
    [onSave, handleClose],
  );

  return {
    initialValues,
    onSubmit,
    enableReinitialize: true,
    validationSchema,
  };
};

export default useFormikProps;
